import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { visaApi } from "@/lib/visa";
import { getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { FileText, Clock, CheckCircle, AlertTriangle, Send, Mail, XCircle, TrendingUp } from "lucide-react";

const COLORS = ["#2A4D3E", "#D97757", "#7A5AC2", "#2980B9", "#B26B00", "#5C6660", "#4A6741", "#C97764"];

export default function VisaDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    visaApi.dashboard().then((d) => { setData(d); setLoading(false); })
      .catch((e) => { toast.error(getApiErrorMessage(e)); setLoading(false); });
  }, []);

  if (loading) return <div className="text-sm text-[#5C6660]">Loading…</div>;
  if (!data) return null;

  const h = data.headline;
  const cards = [
    { label: "Requests today", val: h.requests_today, icon: FileText, color: "#2A4D3E" },
    { label: "Pending verification", val: h.pending_verification, icon: Clock, color: "#B26B00" },
    { label: "Awaiting approval", val: h.awaiting_approval, icon: AlertTriangle, color: "#7A5AC2" },
    { label: "Letters generated", val: h.letters_generated, icon: FileText, color: "#2980B9" },
    { label: "Letters sent", val: h.letters_sent, icon: Send, color: "#D97757" },
    { label: "Visa approved", val: h.visa_approved, icon: CheckCircle, color: "#2A4D3E" },
    { label: "Visa rejected", val: h.visa_rejected, icon: XCircle, color: "#C0392B" },
    { label: "Avg. processing (hrs)", val: h.avg_processing_hours, icon: TrendingUp, color: "#5C6660" },
  ];

  return (
    <div data-testid="visa-dashboard-page">
      <PageHeader eyebrow="International Services" title="Visa Operations"
        subtitle="Live analytics for the visa invitation workflow." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="visa-dash-headline">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white border border-[#E8E6E1] rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold">{c.label}</div>
                <Icon size={14} style={{ color: c.color }} />
              </div>
              <div className="text-2xl font-semibold text-[#2A332C]">{c.val ?? 0}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Country-wise requests">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_country}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#2A4D3E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Hospital-wise requests">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_hospital} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={110} />
              <Tooltip />
              <Bar dataKey="value" fill="#D97757" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Coordinator performance">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.by_coordinator} dataKey="value" nameKey="name" outerRadius={80} label={{ fontSize: 11 }}>
                {(data.by_coordinator || []).map((entry, i) => <Cell key={entry.name || `slice-${i}`} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Monthly trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#2A4D3E" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5">
        <div className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold mb-4">Status funnel</div>
        <div className="space-y-2">
          {(data.status_funnel || []).map((f) => (
            <div key={f.status} className="flex items-center gap-3">
              <div className="w-48 text-xs text-[#5C6660]">{f.label}</div>
              <div className="flex-1 h-6 bg-[#F2F4F3] rounded-full overflow-hidden">
                <div className="h-full bg-[#2A4D3E]" style={{ width: `${Math.min(100, f.count * 10)}%` }} />
              </div>
              <div className="w-10 text-sm font-semibold text-[#2A332C] text-right">{f.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5">
      <div className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}
